import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, Zap, Target, Bot } from "lucide-react";
import { TODAY_PLAN } from "@/data/mock";

const ImpactTag = ({ impact }) => {
  if (!impact) return null;
  const high = impact === "HIGH IMPACT";
  return (
    <span
      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full tracking-wide ${
        high ? "bg-amber-400/20 text-amber-300" : "bg-white/10 text-white/60"
      }`}
    >
      {impact}
    </span>
  );
};

export const DashboardMockup = () => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      className="relative mx-auto max-w-4xl"
      data-testid="hero-dashboard-mockup"
    >
      <div className={`${reduce ? "" : "animate-float"} rounded-3xl border border-white/10 bg-[#0a0a0c]/90 backdrop-blur-xl shadow-2xl overflow-hidden`}>
        {/* window bar */}
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10 bg-white/[0.02]">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-white/20" />
            <span className="h-3 w-3 rounded-full bg-white/20" />
            <span className="h-3 w-3 rounded-full bg-white/20" />
          </div>
          <span className="ml-3 text-xs text-white/40 font-medium">sarkarijobai · JEE Dashboard</span>
        </div>

        <div className="grid md:grid-cols-5 gap-0">
          {/* left: targets */}
          <div className="md:col-span-2 p-6 border-b md:border-b-0 md:border-r border-white/10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-amber-300/80 font-semibold">Your JEE Preparation</p>
            <div className="mt-5 space-y-4">
              <div>
                <p className="text-xs text-white/40">Target</p>
                <p className="font-display text-2xl font-bold text-white">99 <span className="text-sm text-white/50">percentile</span></p>
              </div>
              <div>
                <p className="text-xs text-white/40">Current estimated</p>
                <p className="font-display text-2xl font-bold text-amber-300">96.8 <span className="text-sm text-white/50">percentile</span></p>
              </div>
              <div className="flex gap-6 pt-2">
                <div>
                  <p className="text-xs text-white/40">Current score</p>
                  <p className="font-display text-xl font-bold text-white">142<span className="text-sm text-white/40">/300</span></p>
                </div>
                <div>
                  <p className="text-xs text-white/40">Target score</p>
                  <p className="font-display text-xl font-bold text-white">180+</p>
                </div>
              </div>
              {/* progress */}
              <div className="pt-2">
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "79%" }}
                    transition={{ duration: 1.4, delay: 0.8 }}
                    className="h-full rounded-full bg-gradient-to-r from-amber-400 to-red-500"
                  />
                </div>
                <p className="text-[11px] text-white/40 mt-2">79% toward target trajectory</p>
              </div>
            </div>
          </div>

          {/* right: today's plan */}
          <div className="md:col-span-3 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-4 w-4 text-amber-300" />
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-semibold">Today's highest-impact plan</p>
            </div>
            <div className="space-y-2.5">
              {TODAY_PLAN.map((t, i) => (
                <motion.div
                  key={t.n}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 + i * 0.12 }}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 hover:border-amber-400/30 transition-colors"
                >
                  <span className="font-display text-xs font-bold text-white/30 w-5">{t.n}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{t.topic}</p>
                    <p className="text-[11px] text-white/40">{t.time}</p>
                  </div>
                  <ImpactTag impact={t.impact} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* floating AI coach card */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, delay: 1.3 }}
        className="absolute -bottom-6 -right-2 sm:-right-8 max-w-xs rounded-2xl border border-amber-400/30 bg-[#0a0a0c] p-4 shadow-[0_0_40px_rgba(250,204,21,0.2)] hidden sm:block"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center">
            <Bot className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-xs font-semibold text-amber-300">sarkarijobai AI Coach</span>
        </div>
        <p className="text-xs text-white/70 leading-relaxed">
          "Based on your last 3 mocks, fixing <span className="text-white font-medium">Rotation</span> and{" "}
          <span className="text-white font-medium">Coordinate Geometry</span> offers the highest potential impact on your score."
        </p>
      </motion.div>
    </motion.div>
  );
};
